import { Alert, Box, Button, Stack } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { RemovedFile, UploadModule } from '@/api/files';
import { storageKey, type RecycleBinFilters, type StorageAdmin } from '@/api/storage-admin';
import { daysLeft, formatBytes, FormFields, type FieldDef, type FieldOption } from '@/components/fields';
import { LocalDataTable } from '@/components/grid/LocalDataTable';
import { MODULE_LABELS, serverError } from './common';

const PAGE = 200;

const REASONS: Record<string, string> = {
  REMOVED: 'Removed from its record',
  RECORD_DELETED: 'Its record was deleted',
  RECORD_PURGED: 'Its record left Trash',
  ORPHAN: 'No record kept it',
};
const asOptions = (labels: Record<string, string>): FieldOption[] =>
  Object.entries(labels).map(([value, label]) => ({ value, label }));

const FILTER_FIELDS: FieldDef[] = [
  { type: 'select', name: 'module', label: 'Where', options: asOptions(MODULE_LABELS), config: { emptyLabel: 'Anywhere' },
    grid: { xs: 12, md: 6 } },
  { type: 'select', name: 'reason', label: 'Why it is here', options: asOptions(REASONS), config: { emptyLabel: 'Any reason' },
    grid: { xs: 12, md: 6 } },
];

function where(f: RemovedFile): string {
  const area = MODULE_LABELS[f.module as UploadModule] ?? f.module;
  if (!f.ref_table?.startsWith('dyn:')) return area;
  return `${area} · ${f.ref_table.slice(4)}${f.ref_field ? ` / ${f.ref_field}` : ''}`;
}

/**
 * Every removed file still inside its soft-delete period, across the workspace. A file goes back on its record
 * with Restore, under that record's rules; one whose record is gone can only be downloaded until its period ends.
 */
export function RecycleBinSection({ admin }: { admin: StorageAdmin }) {
  const qc = useQueryClient();
  const [filters, setFilters] = useState<RecycleBinFilters>({});
  const [busy, setBusy] = useState<string>();
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const bin = useInfiniteQuery({
    queryKey: storageKey(admin, 'bin', filters),
    queryFn: ({ pageParam }) => admin.recycleBin!({ ...filters, offset: pageParam, limit: PAGE }),
    initialPageParam: 0,
    getNextPageParam: (page, pages) => (pages.length * PAGE < page.total ? pages.length * PAGE : undefined),
  });
  const rows = bin.data?.pages.flatMap((p) => p.items) ?? [];
  const total = bin.data?.pages[0]?.total ?? 0;

  const act = (id: string, work: () => Promise<unknown>, done?: string) => {
    setBusy(id);
    setError(undefined);
    setNotice(undefined);
    work()
      .then(() => { if (done) setNotice(done); })
      .catch((e) => setError(serverError(e).message))
      .finally(() => setBusy(undefined));
  };
  const restore = (f: RemovedFile) => act(f.id, async () => {
    await admin.restoreFile!(f.id);
    qc.invalidateQueries({ queryKey: storageKey(admin, 'bin') });
    qc.invalidateQueries({ queryKey: ['files', 'recently-removed'] });
  }, `${f.name} is back on its record.`);
  const download = (f: RemovedFile) => act(f.id, async () => {
    window.location.assign((await admin.removedFileUrl!(f.id)).url);
  });

  const columns: GridColDef<RemovedFile>[] = [
    { field: 'name', headerName: 'File', minWidth: 200, flex: 1 },
    { field: 'size', headerName: 'Size', type: 'number', minWidth: 90, valueFormatter: (v: number) => formatBytes(v) },
    { field: 'module', headerName: 'Where', minWidth: 220, valueGetter: (_v, r) => where(r) },
    { field: 'reason', headerName: 'Why', minWidth: 180, valueFormatter: (v: string | null) => (v ? REASONS[v] ?? v : '—') },
    { field: 'deleted_by', headerName: 'Removed by', minWidth: 140, valueFormatter: (v: string | null) => v ?? '—' },
    {
      field: 'deleted_at', headerName: 'Removed on', minWidth: 160,
      valueFormatter: (v: string | null) => (v ? new Date(v).toLocaleString('en-IN') : '—'),
    },
    { field: 'purge_after', headerName: 'Can come back', minWidth: 140, valueFormatter: (v: string | null) => daysLeft(v) },
    {
      field: 'actions', headerName: '', minWidth: 180, sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.5}>
          {row.can_restore && (
            <Button size="small" disabled={!!busy} onClick={() => restore(row)}>Restore</Button>
          )}
          <Button size="small" disabled={!!busy} onClick={() => download(row)}>Download</Button>
        </Stack>
      ),
    },
  ];

  return (
    <Stack spacing={2}>
      <FormFields fields={FILTER_FIELDS} values={filters as Record<string, unknown>}
                  onChange={(name, value) => setFilters((f) => ({ ...f, [name]: value ?? undefined }))} />
      {(bin.error || error) && (
        <Alert severity="error" onClose={() => setError(undefined)}>{error ?? serverError(bin.error).message}</Alert>
      )}
      {notice && <Alert severity="success" onClose={() => setNotice(undefined)}>{notice}</Alert>}
      <LocalDataTable
        rows={rows} columns={columns} getRowId={(r) => r.id} loading={bin.isLoading} wrap
        searchText={(r) => `${r.name} ${r.deleted_by ?? ''} ${r.ref_table ?? ''}`}
        searchPlaceholder="Search file names or who removed them" noun={['file', 'files']}
        emptyMessage="Nothing removed is waiting here."
      />
      {bin.hasNextPage && (
        <Box>
          <Button onClick={() => bin.fetchNextPage()} disabled={bin.isFetchingNextPage}>
            {bin.isFetchingNextPage ? 'Loading…' : `Load more (${rows.length} of ${total})`}
          </Button>
        </Box>
      )}
    </Stack>
  );
}
